import type {
  Point,
  Size,
  ViewTransform,
  ZoomLimits,
} from '@/core/geometry/types'
import { assertPositiveSize } from '@/core/geometry/validate'

export const EDITOR_PADDING_PX = 24
export const VIEW_ZOOM_MIN = 1
export const VIEW_ZOOM_MAX = 8
export const VIEW_MIN_VISIBLE_FRACTION = 0.25
export const FIT_VIEW_TRANSFORM: Readonly<ViewTransform> = Object.freeze({
  scale: VIEW_ZOOM_MIN,
  offsetX: 0,
  offsetY: 0,
})

export function assertViewTransform(transform: ViewTransform): void {
  if (!Number.isFinite(transform.scale)
    || transform.scale < VIEW_ZOOM_MIN
    || transform.scale > VIEW_ZOOM_MAX
    || !Number.isFinite(transform.offsetX)
    || !Number.isFinite(transform.offsetY)) {
    throw new Error(`View transform requires a scale from ${VIEW_ZOOM_MIN} to ${VIEW_ZOOM_MAX} and finite offsets`)
  }
}

export function fitTransform(
  source: Size,
  viewport: Size,
  padding: number = EDITOR_PADDING_PX,
): ViewTransform {
  assertPositiveSize(source, 'Source')
  assertPositiveSize(viewport, 'Viewport')
  if (!Number.isFinite(padding) || padding < 0) throw new Error('Editor padding must be finite and non-negative')
  const availableWidth = viewport.width - 2 * padding
  const availableHeight = viewport.height - 2 * padding
  if (availableWidth <= 0 || availableHeight <= 0) {
    throw new Error(`Editor viewport must be larger than twice its ${padding}px padding`)
  }
  const scale = Math.min(availableWidth / source.width, availableHeight / source.height)

  return {
    scale,
    offsetX: (viewport.width - source.width * scale) / 2,
    offsetY: (viewport.height - source.height * scale) / 2,
  }
}

export function zoomAroundPoint(
  transform: ViewTransform,
  factor: number,
  point: Point,
  limits: ZoomLimits,
): ViewTransform {
  if (!Number.isFinite(transform.scale) || transform.scale <= 0) {
    throw new Error('View transform scale must be finite and positive')
  }
  if (!Number.isFinite(factor) || factor <= 0) {
    throw new Error('Zoom factor must be finite and positive')
  }
  if (limits.minScale <= 0 || limits.maxScale < limits.minScale) {
    throw new Error('Zoom limits are invalid')
  }

  const scale = Math.min(limits.maxScale, Math.max(limits.minScale, transform.scale * factor))
  const sourcePoint = screenToSource(point, transform)
  return {
    scale,
    offsetX: point.x - sourcePoint.x * scale,
    offsetY: point.y - sourcePoint.y * scale,
  }
}

export function composeFitAndViewTransform(
  fit: ViewTransform,
  view: ViewTransform,
): ViewTransform {
  if (!Number.isFinite(fit.scale) || fit.scale <= 0
    || !Number.isFinite(fit.offsetX) || !Number.isFinite(fit.offsetY)) {
    throw new Error('Fit transform must have a finite positive scale and finite offsets')
  }
  assertViewTransform(view)
  return {
    scale: fit.scale * view.scale,
    offsetX: fit.offsetX + view.offsetX,
    offsetY: fit.offsetY + view.offsetY,
  }
}

export function clampViewTransform(
  view: ViewTransform,
  fit: ViewTransform,
  composition: Size,
  viewport: Size,
): ViewTransform {
  assertViewTransform(view)
  assertPositiveSize(composition, 'Composition')
  assertPositiveSize(viewport, 'Viewport')
  if (view.scale === VIEW_ZOOM_MIN) return { ...FIT_VIEW_TRANSFORM }

  const composed = composeFitAndViewTransform(fit, view)
  const width = composition.width * composed.scale
  const height = composition.height * composed.scale
  const visibleWidth = Math.min(width, viewport.width) * VIEW_MIN_VISIBLE_FRACTION
  const visibleHeight = Math.min(height, viewport.height) * VIEW_MIN_VISIBLE_FRACTION
  const left = Math.min(viewport.width - visibleWidth, Math.max(visibleWidth - width, composed.offsetX))
  const top = Math.min(viewport.height - visibleHeight, Math.max(visibleHeight - height, composed.offsetY))
  return {
    scale: view.scale,
    offsetX: left - fit.offsetX,
    offsetY: top - fit.offsetY,
  }
}

export function zoomViewAroundPoint(
  view: ViewTransform,
  factor: number,
  point: Point,
  fit: ViewTransform,
  composition: Size,
  viewport: Size,
): ViewTransform {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    throw new Error('View zoom point must have finite coordinates')
  }
  const composed = composeFitAndViewTransform(fit, view)
  const zoomed = zoomAroundPoint(composed, factor, point, {
    minScale: fit.scale * VIEW_ZOOM_MIN,
    maxScale: fit.scale * VIEW_ZOOM_MAX,
  })
  return clampViewTransform({
    scale: zoomed.scale / fit.scale,
    offsetX: zoomed.offsetX - fit.offsetX,
    offsetY: zoomed.offsetY - fit.offsetY,
  }, fit, composition, viewport)
}

export function panViewTransform(
  view: ViewTransform,
  delta: Point,
  fit: ViewTransform,
  composition: Size,
  viewport: Size,
): ViewTransform {
  if (!Number.isFinite(delta.x) || !Number.isFinite(delta.y)) {
    throw new Error('View pan delta must have finite coordinates')
  }
  if (view.scale === VIEW_ZOOM_MIN) return { ...FIT_VIEW_TRANSFORM }
  return clampViewTransform({
    ...view,
    offsetX: view.offsetX + delta.x,
    offsetY: view.offsetY + delta.y,
  }, fit, composition, viewport)
}

export function screenToSource(point: Point, transform: ViewTransform): Point {
  if (transform.scale <= 0) {
    throw new Error('View transform scale must be positive')
  }
  return {
    x: (point.x - transform.offsetX) / transform.scale,
    y: (point.y - transform.offsetY) / transform.scale,
  }
}

export function sourceToScreen(point: Point, transform: ViewTransform): Point {
  if (transform.scale <= 0) {
    throw new Error('View transform scale must be positive')
  }
  return {
    x: point.x * transform.scale + transform.offsetX,
    y: point.y * transform.scale + transform.offsetY,
  }
}
